import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { createChart, CrosshairMode, AreaSeries, IChartApi, ISeriesApi, ColorType } from "lightweight-charts";
import { ChartOptions, DeepPartial } from "lightweight-charts";
import {
  LegData,
  BacktestSeriesPayload,
  isBacktestSeriesPayload,
} from "../../utils/returns";
import { downloadBacktestAsExcel } from "../../utils/backtestExport";
import { useTheme } from "../../../contexts/ThemeContext";
import styles from './BacktestResultsChart.module.css';
import logger from '../../../logging/logger';

interface ChartDataPoint {
  time: string;
  value: number;
}

interface BacktestResultsChartProps {
  /** Backtest result data (legacy raw leg map or enriched payload) */
  data: LegData | BacktestSeriesPayload;
  height?: number | string;
  /** When true, hides the topbar (metrics/download) so the chart fills the container */
  minimal?: boolean;
}

// Theme constants for dark and light modes
const DARK_THEME = {
  chartBackground: "#0a0b0b",
  chartText: "#e2e8f0",
  chartGrid: "#1f2937",
  chartScaleBorder: "#334155",
  areaLine: "#22c55e",
  areaTop: "rgba(34,197,94,.35)",
  areaBottom: "rgba(34,197,94,0.00)",
};

const LIGHT_THEME = {
  chartBackground: "#ffffff",
  chartText: "#1a1a2e",
  chartGrid: "#e5e7eb",
  chartScaleBorder: "#d1d5db",
  areaLine: "#16a34a",
  areaTop: "rgba(22,163,74,.25)",
  areaBottom: "rgba(22,163,74,0.00)",
};

function getChartOptions(isLightTheme: boolean): DeepPartial<ChartOptions> {
  const theme = isLightTheme ? LIGHT_THEME : DARK_THEME;
  return {
    layout: {
      background: { type: ColorType.Solid, color: theme.chartBackground },
      textColor: theme.chartText,
    },
    grid: {
      vertLines: { color: theme.chartGrid },
      horzLines: { color: theme.chartGrid },
    },
    rightPriceScale: { borderColor: theme.chartScaleBorder },
    timeScale: { borderColor: theme.chartScaleBorder },
    crosshair: { mode: CrosshairMode.Normal },
    localization: { locale: "en-US" },
  };
}

function getAreaOptions(isLightTheme: boolean) {
  const theme = isLightTheme ? LIGHT_THEME : DARK_THEME;
  return {
    lineColor: theme.areaLine,
    topColor: theme.areaTop,
    bottomColor: theme.areaBottom,
    lineWidth: 2 as const,
    priceLineVisible: false,
    lastValueVisible: true,
    crosshairMarkerVisible: true,
  };
}



/**
 * Convert aligned {dates, values} arrays to chart datapoints.
 */
function toChartData(dates: string[] = [], values: number[] = []): ChartDataPoint[] {
  const pointCount = Math.min(dates.length, values.length);
  const points: ChartDataPoint[] = [];
  for (let i = 0; i < pointCount; i++) {
    points.push({ time: dates[i], value: values[i] });
  }
  return points;
}

/**
 * Format cost value: negative = Credit, positive = Debit
 */
function formatCostValue(value: number): string {
  const absValue = Math.abs(value);
  const formattedValue = `$${absValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return value < 0 ? `${formattedValue} Credit` : `${formattedValue} Debit`;
}

export const BacktestResultsChart: React.FC<BacktestResultsChartProps> = ({
  data,
  height = "400px",
  minimal = false,
}) => {
  const { theme } = useTheme();
  const isLightTheme = false; // Always use dark theme for charts
  const wrapRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<ISeriesApi<"Area"> | null>(null);
  const [hoveredInfo, setHoveredInfo] = useState<'metrics' | null>(null);

  const rawData = useMemo<LegData>(() => {
    return isBacktestSeriesPayload(data) ? data.raw_data : data;
  }, [data]);
  const backendPayload = useMemo<BacktestSeriesPayload | null>(() => {
    return isBacktestSeriesPayload(data) ? data : null;
  }, [data]);

  // Multi-symbol results have empty raw_data — disable download in that case
  const hasRawData = useMemo(() => {
    return rawData && Object.keys(rawData).length > 0;
  }, [rawData]);

  // Compute chart data from backend payload (PnL only)
  const chartDataSets = useMemo(() => {
    if (!backendPayload) {
      return null;
    }

    try {
      const dates = backendPayload.dates ?? [];
      const pnlValues = backendPayload.pnl ?? [];

      return {
        pnl_series: toChartData(dates, pnlValues),
        net_capital_gain: backendPayload.net_capital_gain,
        total_trades: backendPayload.total_trades,
        average_cost_per_trade: backendPayload.average_cost_per_trade,
        average_pnl_per_trade: backendPayload.average_pnl_per_trade,
        max_loss: backendPayload.max_loss,
        max_gain: backendPayload.max_gain,
        max_cost: backendPayload.max_cost,
        hit_ratio: backendPayload.hit_ratio,
        max_drawdown: backendPayload.max_drawdown,
        average_profit_percent: backendPayload.average_profit_percent,
        max_profit_percent: backendPayload.max_profit_percent,
        max_loss_percent: backendPayload.max_loss_percent,
      };
    } catch (error) {
      logger.error('BacktestResultsChart: Error computing chart data', error);
      return null;
    }
  }, [backendPayload]);

  // Mount: create chart and area series
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;

    const chart = createChart(el, getChartOptions(isLightTheme));
    const series = chart.addSeries(AreaSeries, getAreaOptions(isLightTheme));

    chartRef.current = chart;
    seriesRef.current = series;

    const resize = () => {
      const r = el.getBoundingClientRect();
      chart.applyOptions({
        width: Math.max(0, Math.floor(r.width || 600)),
        height: Math.max(0, Math.floor(r.height || 400)),
      });
    };
    resize();

    const ro = new ResizeObserver(resize);
    ro.observe(el);

    return () => {
      ro.disconnect();
      chart.remove();
      chartRef.current = null;
      seriesRef.current = null;
    };
  }, [isLightTheme]);

  // Update data when data or theme changes
  useEffect(() => {
    if (!seriesRef.current || !chartDataSets) return;

    seriesRef.current.setData(chartDataSets.pnl_series);

    // Apply price format
    seriesRef.current.applyOptions({
      priceFormat: { type: "price" as const, precision: 2, minMove: 0.01 },
    });

    chartRef.current?.timeScale().fitContent();
  }, [chartDataSets, isLightTheme]);

  // Handle download button click
  const handleDownload = useCallback(() => {
    if (rawData) {
      downloadBacktestAsExcel(rawData);
    }
  }, [rawData]);

  if (!chartDataSets) {
    return (
      <div className={styles.chartWrap} style={{ height }}>
        <div className={styles.noData}>
          No trades were executed during the backtest period.
          <br />
          <span style={{ fontSize: '0.9em', opacity: 0.7, marginTop: '8px', display: 'block' }}>
            This may indicate that entry conditions were not met or the strategy did not generate any signals.
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.chartWrap} style={{ height }}>
      <div className={styles.chartTitle}>Cumulative PnL</div>
      <div className={styles.chartContainer} ref={wrapRef} />
      {!minimal && <div className={styles.topbar}>
        <div className={styles.toggleButtons}>
        </div>
        <div className={styles.capitalInfo}>
            <div className={styles.capitalField}>
              <span className={styles.capitalLabel}>Net Capital Gain:</span>
              <span className={`${styles.capitalValue} ${(chartDataSets.net_capital_gain ?? 0) >= 0 ? styles.positive : styles.negative}`}>
                ${chartDataSets.net_capital_gain?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) ?? 'N/A'}
              </span>
              <div className={styles.infoIconWrapper}>
                <button
                  type="button"
                  className={styles.infoIcon}
                  onMouseEnter={() => setHoveredInfo('metrics')}
                  onMouseLeave={() => setHoveredInfo(null)}
                  aria-label="Trade metrics info"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="16" x2="12" y2="12" />
                    <line x1="12" y1="8" x2="12.01" y2="8" />
                  </svg>
                </button>
                {hoveredInfo === 'metrics' && (
                  <div className={styles.metricsTooltip}>
                    <div className={styles.metricRow}>
                      <span className={styles.metricLabel}>Total Trades:</span>
                      <span className={styles.metricValue}>
                        {chartDataSets.total_trades ?? 0}
                      </span>
                    </div>
                    <div className={styles.metricRow}>
                      <span className={styles.metricLabel}>Avg Cost/Trade:</span>
                      <span className={`${styles.metricValue} ${(chartDataSets.average_cost_per_trade ?? 0) < 0 ? styles.credit : styles.debit}`}>
                        {formatCostValue(chartDataSets.average_cost_per_trade ?? 0)}
                      </span>
                    </div>
                    <div className={styles.metricRow}>
                      <span className={styles.metricLabel}>Avg PnL/Trade:</span>
                      <span className={`${styles.metricValue} ${(chartDataSets.average_pnl_per_trade ?? 0) >= 0 ? styles.positive : styles.negative}`}>
                        ${Math.abs(chartDataSets.average_pnl_per_trade ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div className={styles.metricRow}>
                      <span className={styles.metricLabel}>Max Loss:</span>
                      <span className={`${styles.metricValue} ${styles.negative}`}>
                        ${Math.abs(chartDataSets.max_loss ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div className={styles.metricRow}>
                      <span className={styles.metricLabel}>Max Gain:</span>
                      <span className={`${styles.metricValue} ${styles.positive}`}>
                        ${(chartDataSets.max_gain ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div className={styles.metricRow}>
                      <span className={styles.metricLabel}>Max Cost:</span>
                      <span className={`${styles.metricValue} ${(chartDataSets.max_cost ?? 0) < 0 ? styles.credit : styles.debit}`}>
                        {formatCostValue(chartDataSets.max_cost ?? 0)}
                      </span>
                    </div>
                    <div className={styles.metricRow}>
                      <span className={styles.metricLabel}>Hit Ratio:</span>
                      <span className={styles.metricValue}>
                        {(chartDataSets.hit_ratio ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%
                      </span>
                    </div>
                    <div className={styles.metricRow}>
                      <span className={styles.metricLabel}>Max Drawdown:</span>
                      <span className={`${styles.metricValue} ${styles.negative}`}>
                        ${Math.abs(chartDataSets.max_drawdown ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div className={styles.metricRow}>
                      <span className={styles.metricLabel}>Avg Profit %:</span>
                      <span className={`${styles.metricValue} ${(chartDataSets.average_profit_percent ?? 0) >= 0 ? styles.positive : styles.negative}`}>
                        {(chartDataSets.average_profit_percent ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%
                      </span>
                    </div>
                    <div className={styles.metricRow}>
                      <span className={styles.metricLabel}>Max Profit %:</span>
                      <span className={`${styles.metricValue} ${styles.positive}`}>
                        {(chartDataSets.max_profit_percent ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%
                      </span>
                    </div>
                    <div className={styles.metricRow}>
                      <span className={styles.metricLabel}>Max Loss %:</span>
                      <span className={`${styles.metricValue} ${styles.negative}`}>
                        {(chartDataSets.max_loss_percent ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
      </div>}
      {!minimal && hasRawData && (
        <div className={styles.downloadSection}>
          <button
            type="button"
            className={styles.downloadBtn}
            onClick={handleDownload}
            title="Download backtest results as Excel"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Download Excel
          </button>
        </div>
      )}
    </div>
  );
};
